// src/pages/Landing.jsx
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { createGamesSearchString } from '@/lib/gamesQueryParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronUp, List, Search, Play, Library, Joystick, Sparkles, X } from 'lucide-react';
import SeoManager from '@/components/seo/SeoManager';
import { useTranslation } from '../components/i18n/TranslationProvider';
import { useToast } from '@/components/ui/use-toast';
import useMediaQuery from '@/hooks/useMediaQuery';
import en from '@/components/i18n/locales/en.json';
import { useAuth } from '@/context/AuthContext.jsx';
import { usePublication } from '@/context/PublicationContext.jsx';
import { useFeatureFlags } from '@/context/FeatureFlagContext.jsx';
import { LANDING_SOCIAL_WALL_PREVIEW_FLAG } from '@/components/landing/socialWallLandingPreviewFlag.js';
import useNewsletterPopupTrigger from '@/components/newsletter/useNewsletterPopupTrigger.js';
import PageShowcaseTitle from '@/components/layout/PageShowcaseTitle.jsx';
import { cn } from '@/lib/utils';
import { shouldUseSelectivePublicAccountMode } from '@/lib/selectiveAccountMode.js';
import { trackPublicTelemetryAction } from '@/lib/telemetryClient.js';
import { PUBLIC_TELEMETRY_ACTIONS } from '@/lib/publicTelemetryContract.js';

const ArticleCarousel = lazy(() => import('@/components/blog/ArticleCarousel.jsx'));
const LandingComingSoonRunway = lazy(() => import('@/components/landing/LandingComingSoonRunway.jsx'));
const LandingTrendingShowcase = lazy(() => import('@/components/landing/LandingTrendingShowcase.jsx'));
const LandingHighHypeCountdownShowcase = lazy(() => import('@/components/landing/LandingHighHypeCountdownShowcase.jsx'));
const LandingRecentReleaseDeck = lazy(() => import('@/components/landing/LandingRecentReleaseDeck.jsx'));
const OfficialLiveTeaser = lazy(() => import('@/components/live/OfficialLiveTeaser.jsx'));
const SocialWallLandingPreview = lazy(() => import('@/components/landing/SocialWallLandingPreview.jsx'));
const NewsletterSignupPopup = lazy(() => import('@/components/newsletter/NewsletterSignupPopup.jsx'));

const DEFAULT_ORIGIN = 'https://www.gameplaysync.games';
const ENGLISH_HERO_SUBTITLE = en['landing.subtitle'] || "Where Gamers' Minds Sync";
const LANDING_SECTION_IDS = {
  comingSoon: 'landing-coming-soon',
  cta: 'landing-cta',
  hero: 'landing-hero',
  latestNews: 'landing-latest-news',
  live: 'landing-live',
  mostWanted: 'landing-most-wanted',
  recentlyReleased: 'landing-recently-released',
  socialWall: 'landing-social-wall',
  trending: 'landing-trending',
};
const landingSectionTitleClass =
  "mb-8 px-2 text-center sm:mb-10 lg:mb-12 lg:px-0";
const landingSectionShellClass = "py-20";
const landingSectionContainerClass = "max-w-7xl mx-auto px-4";
const landingGamesSectionMinHeightClass = "min-h-[18rem] sm:min-h-[20rem] md:min-h-[42rem]";
const landingTrendingShowcaseMinHeightClass = "min-h-[23rem] md:min-h-[30rem] lg:min-h-[35rem]";
const landingNewsSectionMinHeightClass = "min-h-[36rem] sm:min-h-[38rem] lg:min-h-[36rem]";
const landingComingSoonSectionMinHeightClass = "min-h-[34rem] md:min-h-[36rem]";
const landingHighHypeCountdownMinHeightClass = "min-h-[34rem] md:min-h-[35rem]";
const landingGameImageSizes = '(min-width: 1280px) 224px, (min-width: 1024px) 18vw, (min-width: 768px) 22vw, 44vw';
const GAME_IMAGE_BASE_URL = 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images';
const gameImageUrl = (fileName) => `${GAME_IMAGE_BASE_URL}/${fileName}`;
const gamePreviewScreenshotUrls = (fileName, count = 4) =>
  Array.from({ length: count }, (_, index) => gameImageUrl(`${fileName}?codex-preview-shot=${index + 1}`));
const landingTrendingAgeRatingPreviewGames = [
  {
    age_ratings: [
      'PEGI: 7',
      'CERO: A',
      'USK: 12',
      'ESRB: E10+',
      'GRAC: All',
      'ClassInd: 10',
      'ACB: G',
      'OFLC NZ: PG',
    ],
    cover_image: gameImageUrl('rhythm-heaven-groove-cover.avif'),
    franchises: ['Rhythm Heaven'],
    game_engines: ['Unreal Engine 5'],
    game_modes: ['Single player'],
    genres: ['Music'],
    header_image: gameImageUrl('rhythm-heaven-groove-cover.avif'),
    id: 'codex-trending-preview-rhythm-heaven',
    platforms: ['Nintendo Switch 2', 'Nintendo Switch', 'PC (Microsoft Windows)', 'PlayStation 5', 'Xbox Series X|S'],
    player_perspectives: ['Side view'],
    screenshots: gamePreviewScreenshotUrls('rhythm-heaven-groove-cover.avif', 5),
    series: ['Rhythm Heaven'],
    slug: 'rhythm-heaven-groove',
    store_links: [
      { platform: 'Nintendo eShop', url: 'https://www.nintendo.com/' },
      { platform: 'Steam', url: 'https://store.steampowered.com/' },
    ],
    themes: ['Action', 'Comedy'],
    title: 'Rhythm Heaven Groove',
  },
  {
    age_ratings: ['PEGI: 12', 'ESRB: T'],
    cover_image: gameImageUrl('star-fox--5-cover.avif'),
    franchises: ['Star Fox'],
    game_modes: ['Single player', 'Multiplayer'],
    genres: ['Action', 'Shooter'],
    header_image: gameImageUrl('star-fox--5-header.avif'),
    id: 'codex-trending-preview-star-fox',
    platforms: ['PC', 'PlayStation 5'],
    player_perspectives: ['Third person'],
    screenshots: gamePreviewScreenshotUrls('star-fox--5-header.avif', 5),
    series: ['Star Fox'],
    slug: 'star-fox--5',
    store_links: [
      { platform: 'Nintendo eShop', url: 'https://www.nintendo.com/' },
      { platform: 'Steam', url: 'https://store.steampowered.com/' },
      { platform: 'Twitch', url: 'https://www.twitch.tv/' },
      { platform: 'YouTube', url: 'https://www.youtube.com/' },
      { platform: 'X / Twitter', url: 'https://x.com/' },
      { platform: 'Discord', url: 'https://discord.com/' },
      { platform: 'Reddit', url: 'https://www.reddit.com/' },
      { platform: 'Wikipedia', url: 'https://www.wikipedia.org/' },
    ],
    themes: ['Sci-fi', 'Arcade'],
    title: 'Star Fox',
  },
  {
    age_ratings: ['PEGI: 16', 'ESRB: M', 'CERO: D', 'USK: 16', 'ACB: MA15'],
    cover_image: gameImageUrl('directive-8020-cover.avif'),
    franchises: ['The Dark Pictures'],
    game_modes: ['Single player'],
    genres: ['Adventure', 'Horror'],
    header_image: gameImageUrl('directive-8020-cover.avif'),
    id: 'codex-trending-preview-directive',
    platforms: ['PC (Microsoft Windows)', 'PlayStation 5', 'Xbox Series X|S'],
    player_perspectives: ['Third person'],
    screenshots: gamePreviewScreenshotUrls('directive-8020-cover.avif', 5),
    series: ['The Dark Pictures Anthology'],
    slug: 'directive-8020',
    store_links: [
      { platform: 'Steam', url: 'https://store.steampowered.com/' },
      { platform: 'PlayStation Store', url: 'https://store.playstation.com/' },
      { platform: 'Xbox Store', url: 'https://www.xbox.com/' },
      { platform: 'Epic Games Store', url: 'https://store.epicgames.com/' },
      { platform: 'YouTube', url: 'https://www.youtube.com/' },
    ],
    themes: ['Survival', 'Drama'],
    title: 'Directive 8020',
  },
  {
    age_ratings: [
      'PEGI: 18',
      'ESRB: M',
      'USK: 18',
      'CERO: Z',
      'ACB: R18+',
      'GRAC: 19+',
      'ClassInd: 18',
      'OFLC NZ: R18+',
    ],
    cover_image: gameImageUrl('the-blood-of-dawnwalker-cover.avif'),
    franchises: ['Dawnwalker'],
    game_engines: ['Unreal Engine 5'],
    game_modes: ['Single player'],
    genres: ['Action', 'RPG'],
    header_image: gameImageUrl('the-blood-of-dawnwalker-cover.avif'),
    id: 'codex-trending-preview-heavy-collision',
    platforms: ['PC (Microsoft Windows)', 'PlayStation 5', 'Xbox Series X|S', 'Nintendo Switch 2', 'Nintendo Switch'],
    player_perspectives: ['Third person'],
    screenshots: gamePreviewScreenshotUrls('the-blood-of-dawnwalker-cover.avif', 5),
    series: ['Dawnwalker'],
    slug: 'the-blood-of-dawnwalker',
    store_links: [
      { platform: 'Steam', url: 'https://store.steampowered.com/' },
      { platform: 'GOG', url: 'https://www.gog.com/' },
      { platform: 'Epic Games Store', url: 'https://store.epicgames.com/' },
      { platform: 'PlayStation Store', url: 'https://store.playstation.com/' },
      { platform: 'Xbox Store', url: 'https://www.xbox.com/' },
      { platform: 'YouTube', url: 'https://www.youtube.com/' },
      { platform: 'Discord', url: 'https://discord.com/' },
      { platform: 'Reddit', url: 'https://www.reddit.com/' },
    ],
    themes: ['Fantasy', 'Survival'],
    title: 'The Blood of Dawnwalker',
  },
];
const landingHighHypeCountdownPreviewGames = [
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-blood-of-dawnwalker-cover.avif',
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-blood-of-dawnwalker-cover.avif',
    hype: 183,
    id: 'codex-high-hype-dawnwalker',
    release_date: '2026-09-03T00:00:00+00:00',
    slug: 'the-blood-of-dawnwalker',
    title: 'The Blood of Dawnwalker',
    video_urls: ['https://www.youtube.com/watch?v=eeH0HMqd9VY'],
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/control-resonant--1-cover.avif',
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/control-resonant--1-cover.avif',
    hype: 120,
    id: 'codex-high-hype-control-resonant',
    release_date: '2026-09-24T00:00:00+00:00',
    slug: 'control-resonant--1',
    title: 'Control Resonant',
    video_urls: ['https://www.youtube.com/watch?v=e1_eZyFM5-4'],
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/beast-of-reincarnation-cover.avif',
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/beast-of-reincarnation-cover.avif',
    hype: 106,
    id: 'codex-high-hype-beast',
    release_date: '2026-08-04T00:00:00+00:00',
    slug: 'beast-of-reincarnation',
    title: 'Beast of Reincarnation',
    video_urls: ['https://www.youtube.com/watch?v=ysyYwLbDByU'],
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/metal-gear-solid-master-collection-volume-2--1-cover.avif',
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/metal-gear-solid-master-collection-volume-2--1-header.avif',
    hype: 26,
    id: 'codex-high-hype-metal-gear',
    release_date: '2026-08-27T00:00:00+00:00',
    slug: 'metal-gear-solid-master-collection-volume-2--1',
    title: 'Metal Gear Solid Master Collection: Volume 2',
    video_urls: ['https://www.youtube.com/watch?v=a3AujdsJvjI'],
  },
];
const landingComingSoonRunwayPreviewGames = [
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/star-fox--5-cover.avif',
    genres: ['Action', 'Sci-fi'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/star-fox--5-header.avif',
    id: 'codex-coming-runway-star-fox',
    platforms: ['Nintendo Switch 2', 'Nintendo Switch', 'PC', 'PlayStation 5', 'Xbox Series X|S'],
    release_date: '2026-07-20T00:00:00+00:00',
    slug: 'star-fox--5',
    title: 'Star Fox',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-adventures-of-elliot-the-millennium-tales-cover.avif',
    genres: ['RPG', 'Adventure'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-adventures-of-elliot-the-millennium-tales-header.avif',
    id: 'codex-coming-runway-elliot',
    platforms: ['Nintendo Switch 2', 'PC'],
    release_date: '2026-07-21T00:00:00+00:00',
    slug: 'the-adventures-of-elliot-the-millennium-tales',
    title: 'The Adventures of Elliot: The Millennium Tales',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/bubsy-4d-cover.avif',
    genres: ['Platform', 'Adventure'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/bubsy-4d-cover.avif',
    id: 'codex-coming-runway-bubsy',
    platforms: ['Nintendo Switch 2', 'PC'],
    release_date: '2026-07-21T00:00:00+00:00',
    slug: 'bubsy-4d',
    title: 'Bubsy 4D',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/directive-8020-cover.avif',
    genres: ['Horror', 'Adventure'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/directive-8020-cover.avif',
    id: 'codex-coming-runway-directive',
    platforms: ['PC', 'Xbox Series X|S'],
    release_date: '2026-07-22T00:00:00+00:00',
    slug: 'directive-8020',
    title: 'Directive 8020',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/saros-cover.avif',
    genres: ['Action', 'Shooter'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/saros-cover.avif',
    id: 'codex-coming-runway-saros',
    platforms: ['PlayStation 5'],
    release_date: '2026-07-23T00:00:00+00:00',
    slug: 'saros',
    title: 'Saros',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/rhythm-heaven-groove-cover.avif',
    genres: ['Rhythm', 'Party'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/rhythm-heaven-groove-cover.avif',
    id: 'codex-coming-runway-rhythm-heaven',
    platforms: ['Nintendo Switch 2'],
    release_date: '2026-07-23T00:00:00+00:00',
    slug: 'rhythm-heaven-groove',
    title: 'Rhythm Heaven Groove',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/deer-and-boy-cover.avif',
    genres: ['Adventure', 'Narrative'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/deer-and-boy-cover.avif',
    id: 'codex-coming-runway-deer-boy',
    platforms: ['PC', 'PlayStation 5'],
    release_date: '2026-07-23T00:00:00+00:00',
    slug: 'deer-and-boy',
    title: 'Deer & Boy',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/solarpunk-cover.avif',
    genres: ['Survival', 'Crafting'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/solarpunk-cover.avif',
    id: 'codex-coming-runway-solarpunk',
    platforms: ['PC', 'Xbox Series X|S'],
    release_date: '2026-07-23T00:00:00+00:00',
    slug: 'solarpunk',
    title: 'Solarpunk',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/diablo-iv-lord-of-hatred-cover.avif',
    genres: ['Action RPG'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/diablo-iv-lord-of-hatred-cover.avif',
    id: 'codex-coming-runway-diablo',
    platforms: ['PC', 'PlayStation 5', 'Xbox Series X|S'],
    release_date: '2026-07-23T00:00:00+00:00',
    slug: 'diablo-iv-lord-of-hatred',
    title: 'Diablo IV: Lord of Hatred',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/beast-of-reincarnation-cover.avif',
    genres: ['Action RPG'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/beast-of-reincarnation-cover.avif',
    id: 'codex-coming-runway-beast',
    platforms: ['Xbox Series X|S', 'PC'],
    release_date: '2026-07-24T00:00:00+00:00',
    slug: 'beast-of-reincarnation',
    title: 'Beast of Reincarnation',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/metal-gear-solid-master-collection-volume-2--1-cover.avif',
    genres: ['Action', 'Stealth'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/metal-gear-solid-master-collection-volume-2--1-header.avif',
    id: 'codex-coming-runway-metal-gear',
    platforms: ['PlayStation 5', 'PC'],
    release_date: '2026-07-25T00:00:00+00:00',
    slug: 'metal-gear-solid-master-collection-volume-2--1',
    title: 'Metal Gear Solid Master Collection: Volume 2',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-blood-of-dawnwalker-cover.avif',
    genres: ['RPG', 'Action'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-blood-of-dawnwalker-cover.avif',
    id: 'codex-coming-runway-dawnwalker',
    platforms: ['PlayStation 5', 'Xbox Series X|S'],
    release_date: '2026-07-25T00:00:00+00:00',
    slug: 'the-blood-of-dawnwalker',
    title: 'The Blood of Dawnwalker',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/killer-bean-cover.avif',
    genres: ['Shooter', 'Action'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/killer-bean-cover.avif',
    id: 'codex-coming-runway-killer-bean',
    platforms: ['PC'],
    release_date: '2026-07-25T00:00:00+00:00',
    slug: 'killer-bean',
    title: 'Killer Bean',
  },
  {
    cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/control-resonant--1-cover.avif',
    genres: ['Action', 'Adventure'],
    header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/control-resonant--1-cover.avif',
    id: 'codex-coming-runway-control-resonant',
    platforms: ['PC', 'PlayStation 5'],
    release_date: '2026-07-26T00:00:00+00:00',
    slug: 'control-resonant--1',
    title: 'Control Resonant',
  },
];
const landingLatestNewsPreviewSourcePosts = [
  {
    game: {
      cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/star-fox--5-cover.avif',
      header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/star-fox--5-header.avif',
      id: 'codex-news-preview-star-fox-game',
      igdb_id: 400709,
      slug: 'star-fox--5',
      title: 'Star Fox',
    },
    post: {
      excerpt: 'Star Fox returns on Nintendo Switch 2 with modern presentation upgrades, but the review weighs how much the refreshed look actually lifts the core play.',
      excerpt_it: 'Star Fox torna su Nintendo Switch 2 con una veste piu moderna, ma la recensione pesa quanto il rinnovamento visivo riesca davvero a sostenere il gameplay.',
      generated_date: '2026-06-25T06:21:50.071+00:00',
      id: 'codex-news-preview-star-fox',
      igdb_id: 400709,
      main_tag: 'review',
      scores: {
        overall: 70,
      },
      slug: 'star-fox-a-visually-refreshed-classic-struggles-to-elevate-gameplay',
      status: 'published',
      title: 'Star Fox: A Visually Refreshed Classic Struggles to Elevate Gameplay',
      title_it: 'Star Fox: Un classico rinnovato visivamente che fatica ad elevare il gameplay',
    },
  },
  {
    game: {
      cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-adventures-of-elliot-the-millennium-tales-cover.avif',
      header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/the-adventures-of-elliot-the-millennium-tales-header.avif',
      id: 'codex-news-preview-elliot-game',
      igdb_id: 358534,
      slug: 'the-adventures-of-elliot-the-millennium-tales',
      title: 'The Adventures of Elliot: The Millennium Tales',
    },
    post: {
      excerpt: 'Square Enix frames Elliot as an action RPG built around striking HD-2D presentation and layered exploration, with early strengths tied to its sense of place.',
      excerpt_it: 'Square Enix costruisce Elliot come action RPG centrato su una forte identita visiva HD-2D e su un esplorazione stratificata, con punti di forza gia leggibili.',
      generated_date: '2026-06-18T06:03:07.479+00:00',
      id: 'codex-news-preview-elliot',
      igdb_id: 358534,
      main_tag: 'review',
      scores: {
        overall: 72,
      },
      slug: 'the-adventures-of-elliot-the-millennium-tales-a-journey-through-intricate-gameplay-and-striking-visuals',
      status: 'published',
      title: 'The Adventures of Elliot: The Millennium Tales: A Journey Through Intricate Gameplay and Striking Visuals',
      title_it: 'The Adventures of Elliot: The Millennium Tales: Un viaggio attraverso un gameplay intricato e una grafica sorprendente',
    },
  },
  {
    game: {
      cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/bubsy-4d-cover.avif',
      header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/bubsy-4d-header.avif',
      id: 'codex-news-preview-bubsy-game',
      igdb_id: 361820,
      slug: 'bubsy-4d',
      title: 'Bubsy 4D',
    },
    post: {
      excerpt: 'Bubsy 4D brings the bobcat back with a brighter 3D platforming pitch, balancing nostalgic appeal against the familiar questions around precision and tone.',
      excerpt_it: 'Bubsy 4D riporta in scena il personaggio con una piattaforma 3D piu vivace, tra richiamo nostalgico e dubbi su precisione, ritmo e identita.',
      generated_date: '2026-05-22T06:22:50.891+00:00',
      id: 'codex-news-preview-bubsy',
      igdb_id: 361820,
      main_tag: 'review',
      scores: null,
      slug: 'bubsy-4d-a-bright-yet-controversial-revival',
      status: 'published',
      title: 'Bubsy 4D: A Bright Yet Controversial Revival',
      title_it: 'Bubsy 4D: un rilancio brillante ma controverso',
    },
  },
  {
    game: {
      cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/directive-8020-cover.avif',
      header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/directive-8020-cover.avif',
      id: 'codex-news-preview-directive-game',
      igdb_id: 237780,
      slug: 'directive-8020',
      title: 'Directive 8020',
    },
    post: {
      excerpt: 'Directive 8020 uses sci-fi horror, isolation, and the Tau Ceti setting to test how much new tension the anthology formula can still produce.',
      excerpt_it: 'Directive 8020 usa horror fantascientifico, isolamento e lo scenario di Tau Ceti per misurare quanta tensione nuova possa ancora generare la formula antologica.',
      generated_date: '2026-05-12T06:41:42.625+00:00',
      id: 'codex-news-preview-directive',
      igdb_id: 237780,
      main_tag: 'review',
      scores: null,
      slug: 'directive-8020-a-struggle-between-innovation-and-tradition-in-sci-fi-horror',
      status: 'published',
      title: 'Directive 8020: A Struggle Between Innovation and Tradition in Sci-Fi Horror',
      title_it: "Directive 8020: Uno scontro tra innovazione e tradizione nell'horror fantascientifico",
    },
  },
  {
    game: {
      cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/saros-cover.avif',
      header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/saros-cover.avif',
      id: 'codex-news-preview-saros-game',
      igdb_id: 331215,
      slug: 'saros',
      title: 'Saros',
    },
    post: {
      excerpt: 'Saros leans into Carcosa, eclipse imagery, and a darker PlayStation identity, turning its premise into a compact test of atmosphere and combat pressure.',
      excerpt_it: 'Saros insiste su Carcosa, sull immaginario dell eclissi e su una identita PlayStation piu cupa, tra atmosfera e pressione del combattimento.',
      generated_date: '2026-04-30T06:04:59.994+00:00',
      id: 'codex-news-preview-saros',
      igdb_id: 331215,
      main_tag: 'review',
      scores: null,
      slug: 'saros-a-journey-into-the-heart-of-darkness',
      status: 'published',
      title: 'Saros: A Journey into the Heart of Darkness',
      title_it: 'Saros: Un Viaggio nel Cuore delle Tenebre',
    },
  },
  {
    game: {
      cover_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/diablo-iv-lord-of-hatred-cover.avif',
      header_image: 'https://eimwvzunwxgdfxipoqbk.supabase.co/storage/v1/object/public/game-images/diablo-iv-lord-of-hatred-cover.avif',
      id: 'codex-news-preview-diablo-game',
      igdb_id: 302381,
      slug: 'diablo-iv-lord-of-hatred',
      title: 'Diablo IV: Lord of Hatred',
    },
    post: {
      excerpt: 'Diablo IV: Lord of Hatred returns to Sanctuary with a harsher tone, using familiar action RPG foundations to frame a darker expansion beat.',
      excerpt_it: 'Diablo IV: Lord of Hatred torna a Sanctuary con un taglio piu crudo, usando basi action RPG familiari per sostenere una nuova spinta oscura.',
      generated_date: '2026-04-28T06:24:06.355+00:00',
      id: 'codex-news-preview-diablo',
      igdb_id: 302381,
      main_tag: 'review',
      scores: null,
      slug: 'diablo-iv-lord-of-hatred-a-gritty-return-to-sanctuary',
      status: 'published',
      title: 'Diablo IV: Lord of Hatred: A Gritty Return to Sanctuary',
      title_it: 'Diablo IV: Lord of Hatred: Un Ritorno Crudo a Sanctuary',
    },
  },
];
const landingLatestNewsPreviewPosts = [
  ...landingLatestNewsPreviewSourcePosts.slice(1),
  ...landingLatestNewsPreviewSourcePosts.slice(1).map((entry, index) => ({
    game: {
      ...entry.game,
      id: `${entry.game.id}-extended-${index + 1}`,
    },
    post: {
      ...entry.post,
      generated_date: `2026-04-${String(20 - index).padStart(2, '0')}T06:00:00.000+00:00`,
      id: `${entry.post.id}-extended-${index + 1}`,
      slug: `${entry.post.slug}-extended-${index + 1}`,
    },
  })),
].slice(0, 10);
const landingRecentReleaseDeckPreviewLimit = 20;
const landingRecentReleaseDeckPreviewMeta = [
  {
    ageRatings: ['PEGI: 12', 'ESRB: T', 'USK: 12', 'CERO: B'],
    genres: ['Action'],
    platforms: ['Nintendo Switch 2', 'Nintendo Switch', 'PC', 'PlayStation 5', 'Xbox Series X|S'],
    releaseDate: '2026-07-05T00:00:00+00:00',
    score: 77,
    themes: ['Sci-fi'],
  },
  {
    ageRatings: ['PEGI: 7', 'ESRB: E', 'USK: 6', 'CERO: A', 'GRAC: All'],
    genres: ['RPG', 'Adventure', 'Action', 'Roguelite'],
    platforms: ['Nintendo Switch 2', 'Nintendo Switch', 'PC', 'PlayStation 5', 'Xbox Series X|S'],
    releaseDate: '2026-07-04T00:00:00+00:00',
    score: 73,
    themes: ['Fantasy', 'Cartoon', 'Sci-fi'],
  },
  {
    ageRatings: ['PEGI: 3', 'ESRB: E', 'ClassInd: L'],
    genres: ['Platform', 'Adventure'],
    platforms: ['Nintendo Switch 2', 'PC'],
    releaseDate: '2026-07-03T00:00:00+00:00',
    score: 76,
    themes: ['Cartoon'],
  },
  {
    ageRatings: ['PEGI: 16', 'ESRB: M', 'USK: 16', 'CERO: D'],
    genres: ['Horror'],
    platforms: ['PC', 'Xbox Series X|S'],
    releaseDate: '2026-07-02T00:00:00+00:00',
    score: 81,
    themes: ['Sci-fi'],
  },
  {
    ageRatings: ['PEGI: 16', 'ESRB: T', 'USK: 16', 'CERO: C', 'ACB: M'],
    genres: ['Action', 'Roguelite'],
    platforms: ['PlayStation 5'],
    releaseDate: '2026-07-01T00:00:00+00:00',
    score: 84,
    themes: ['Dark fantasy'],
  },
  {
    ageRatings: ['PEGI: 18', 'ESRB: M', 'USK: 18', 'CERO: Z', 'GRAC: 19+', 'ClassInd: 18', 'ACB: R18+', 'OFLC (NZ): R18+'],
    genres: ['Action RPG'],
    platforms: ['PC', 'PlayStation 5'],
    releaseDate: '2026-06-30T00:00:00+00:00',
    score: 78,
    themes: ['Dark fantasy'],
  },
];
const formatReleaseDeckPreviewDate = (index) => {
  const date = new Date(Date.UTC(2026, 6, 8));
  date.setUTCDate(date.getUTCDate() - index);
  return date.toISOString();
};
const landingRecentReleaseDeckPreviewSource = landingLatestNewsPreviewPosts.length
  ? landingLatestNewsPreviewPosts
  : landingLatestNewsPreviewSourcePosts;
const landingRecentReleaseDeckPreviewGames = Array.from(
  { length: landingRecentReleaseDeckPreviewLimit },
  (_, index) => {
    const { game, post } = landingRecentReleaseDeckPreviewSource[
      index % landingRecentReleaseDeckPreviewSource.length
    ];
    const meta = landingRecentReleaseDeckPreviewMeta[index % landingRecentReleaseDeckPreviewMeta.length] || {};
    const overflowLabel = index >= landingRecentReleaseDeckPreviewSource.length ? ` ${index + 1}` : '';

    return {
      ...game,
      age_ratings: meta.ageRatings || [],
      gameplaysync_score: meta.score || post?.scores?.overall,
      genres: meta.genres || [],
      id: `codex-release-deck-${game.id}-${index + 1}`,
      platforms: meta.platforms || [],
      release_date: formatReleaseDeckPreviewDate(index),
      slug: `${game.slug}-release-preview-${index + 1}`,
      themes: meta.themes || [],
      title: `${game.title}${overflowLabel}`,
    };
  },
);

function renderLandingSectionTitle(title) {
  return (
    <PageShowcaseTitle as="h2" className={landingSectionTitleClass} title={title} />
  );
}

function renderLandingSection(title, content, minHeightClass, options = {}) {
  const { id, sectionStyle, sectionClassName = '' } = options;
  return (
    <section className={`${landingSectionShellClass} ${sectionClassName}`.trim()} id={id} style={sectionStyle}>
      <div className={landingSectionContainerClass}>
        {renderLandingSectionTitle(title)}
        <div className={minHeightClass}>{content}</div>
      </div>
    </section>
  );
}

const LandingSectionNavigator = ({
  activeSectionId,
  isMobileOpen,
  onNavigate,
  onToggleMobile,
  sections,
  t,
  triggerRef,
  hideMobile,
}) => {
  const mobileCloseButtonRef = useRef(null);

  useEffect(() => {
    if (isMobileOpen) {
      mobileCloseButtonRef.current?.focus();
    }
  }, [isMobileOpen]);

  if (sections.length < 2) {
    return null;
  }

  const activeSection = sections.find((section) => section.id === activeSectionId) || sections[0];

  const renderDot = (sectionId) => (
    <span
      aria-hidden="true"
      className={cn(
        'h-2 w-2 shrink-0 rounded-full transition',
        activeSectionId === sectionId
          ? 'bg-cyan-200 shadow-[0_0_14px_rgba(165,243,252,0.8)]'
          : 'bg-slate-500 group-hover:bg-slate-300',
      )}
    />
  );

  return (
    <>
      <nav
        aria-label={t('landing.section_nav.label')}
        className="fixed left-3 top-1/2 z-40 hidden -translate-y-1/2 lg:block"
      >
        <div className="group rounded-full border border-white/10 bg-slate-950/82 p-2 shadow-2xl shadow-black/35 backdrop-blur-md transition-all duration-200 hover:rounded-lg hover:bg-slate-950/92">
          <div className="flex w-8 flex-col gap-1 overflow-hidden transition-[width] duration-200 group-hover:w-44">
            {sections.map((section) => (
              <button
                aria-current={activeSectionId === section.id ? 'true' : undefined}
                className={cn(
                  'flex h-8 min-w-0 items-center gap-3 rounded-full px-3 text-left text-xs font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white',
                  activeSectionId === section.id && 'bg-cyan-300/12 text-cyan-50',
                )}
                key={section.id}
                onClick={() => onNavigate(section.id)}
                title={section.label}
                type="button"
              >
                {renderDot(section.id)}
                <span className="truncate opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  {section.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {!hideMobile ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+0.95rem)] z-40 flex justify-center px-3 lg:hidden">
          {isMobileOpen ? (
            <div
              aria-label={t('landing.section_nav.label')}
              className="pointer-events-auto w-full max-w-sm rounded-lg border border-white/10 bg-slate-950/92 p-3 text-white shadow-2xl shadow-black/45 backdrop-blur-md"
              role="dialog"
            >
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                  {t('landing.section_nav.mobile_title')}
                </p>
                <Button
                  ref={mobileCloseButtonRef}
                  aria-label={t('landing.section_nav.close')}
                  className="h-11 min-h-11 w-11 rounded-full border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"
                  onClick={() => onToggleMobile(false)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {sections.map((section) => (
                  <button
                    aria-current={activeSectionId === section.id ? 'true' : undefined}
                    className={cn(
                      'flex min-h-11 min-w-0 items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-2 text-left text-xs font-semibold text-slate-200 transition hover:bg-white/[0.08]',
                      activeSectionId === section.id && 'border-cyan-200/45 bg-cyan-300/12 text-cyan-50',
                    )}
                    key={section.id}
                    onClick={() => onNavigate(section.id)}
                    type="button"
                  >
                    {renderDot(section.id)}
                    <span className="truncate">{section.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              ref={triggerRef}
              aria-label={t('landing.section_nav.open')}
              className="pointer-events-auto inline-flex min-h-11 max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-lg border border-cyan-200/30 bg-slate-950/92 px-3 py-2 text-left text-white shadow-2xl shadow-black/40 ring-1 ring-cyan-200/10 backdrop-blur-md transition hover:bg-slate-900/95"
              onClick={() => onToggleMobile(true)}
              title={t('landing.section_nav.open')}
              type="button"
            >
              <span
                aria-hidden="true"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-cyan-300/15 text-cyan-100"
              >
                <List className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[0.62rem] font-black uppercase leading-none tracking-wide text-cyan-100">
                  {t('landing.section_nav.mobile_title')}
                </span>
                <span className="mt-1 block max-w-[11rem] truncate text-xs font-semibold leading-none text-white">
                  {activeSection.label}
                </span>
              </span>
              <span aria-hidden="true" className="ml-1 flex shrink-0 items-center gap-1">
                {renderDot(activeSection.id)}
                <ChevronUp className="h-4 w-4 text-slate-300" />
              </span>
            </button>
          )}
        </div>
      ) : null}
    </>
  );
};

function renderLandingGameGridSkeleton(count = 10) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5">
      {Array.from({ length: count }).map((_, index) => (
        <div key={`landing-game-grid-skeleton-${index}`} className="glass-effect overflow-hidden rounded-2xl">
          <Skeleton className="aspect-[3/4] w-full rounded-none bg-white/10 md:h-72 md:aspect-auto" />
        </div>
      ))}
    </div>
  );
}

function renderLandingGameCarouselSkeleton(count = 2, itemsPerView = 2) {
  const basisMap = {
    2: 'md:basis-[calc((100%-1rem)/2)]',
    3: 'md:basis-[calc((100%-2rem)/3)]',
    4: 'md:basis-[calc((100%-3rem)/4)]',
    5: 'md:basis-[calc((100%-4rem)/5)]',
  };
  const desktopBasisClass = basisMap[itemsPerView] || basisMap[5];

  return (
    <div>
      <div className="flex gap-4 overflow-hidden px-2">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={`landing-game-carousel-skeleton-${index}`}
            className={`basis-[calc(50%-0.5rem)] flex-shrink-0 overflow-hidden rounded-2xl ${desktopBasisClass}`}
          >
            <div className="aspect-[3/4] w-full overflow-hidden rounded-2xl md:h-[290px] md:aspect-auto">
              <Skeleton className="h-full w-full rounded-2xl bg-white/10" />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 h-1 w-full rounded-full bg-gray-700/50" />
    </div>
  );
}

function renderLandingComingSoonRunwaySkeleton() {
  return (
    <div
      className="relative overflow-hidden rounded-lg border border-cyan-200/14 bg-slate-950/86 shadow-2xl shadow-black/30"
      data-testid="landing-coming-soon-runway-skeleton"
    >
      <div className="gps-landing-runway-skeleton-backdrop absolute inset-0" />
      <div className="relative z-10 grid gap-5 p-4 sm:p-5 lg:grid-cols-[minmax(14rem,0.48fr)_minmax(0,1fr)] lg:p-7 xl:p-8">
        <aside className="flex min-h-[26rem] flex-col justify-between rounded-lg border border-white/10 bg-slate-950/46 p-5 shadow-2xl shadow-black/25 backdrop-blur-xl lg:min-h-[31rem]">
          <div>
            <Skeleton className="h-3 w-32 bg-white/10" />
            <Skeleton className="mt-5 h-10 w-48 bg-white/10" />
            <Skeleton className="mt-3 h-10 w-36 bg-white/10" />
            <Skeleton className="mt-4 h-4 w-44 bg-white/10" />
          </div>
          <div className="mt-8 space-y-5">
            <Skeleton className="h-20 rounded-lg bg-white/10" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-7 w-20 rounded-full bg-white/10" />
              <Skeleton className="h-7 w-24 rounded-full bg-white/10" />
              <Skeleton className="h-7 w-16 rounded-full bg-white/10" />
            </div>
            <Skeleton className="h-10 w-36 rounded-full bg-white/10" />
          </div>
        </aside>
        <div className="min-w-0">
          <div className="flex gap-4 overflow-hidden px-1 pb-5 pt-1 md:gap-5 lg:pb-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                className="h-[28rem] w-[78vw] max-w-[20rem] shrink-0 rounded-lg border border-white/10 bg-slate-950/76 p-4 shadow-2xl shadow-black/30 sm:w-[18rem] lg:h-[31rem] lg:w-[19rem]"
                key={`landing-coming-runway-skeleton-card-${index}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <Skeleton className="h-7 w-28 rounded-full bg-white/10" />
                  <Skeleton className="h-7 w-16 rounded-full bg-white/10" />
                </div>
                <Skeleton className="mx-auto mt-7 aspect-[3/4] w-[70%] rounded-lg bg-white/10 lg:mt-8" />
                <div className="mt-7 rounded-lg border border-white/10 bg-white/[0.035] p-4">
                  <Skeleton className="h-3 w-24 bg-white/10" />
                  <Skeleton className="mt-4 h-7 w-full bg-white/10" />
                  <Skeleton className="mt-2 h-7 w-3/4 bg-white/10" />
                  <div className="mt-4 flex gap-2">
                    <Skeleton className="h-7 w-16 rounded-full bg-white/10" />
                    <Skeleton className="h-7 w-20 rounded-full bg-white/10" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 border-t border-white/10 pt-4">
            <Skeleton className="h-4 w-4 rounded-full bg-white/10" />
            <Skeleton className="h-1.5 flex-1 rounded-full bg-white/10" />
          </div>
        </div>
      </div>
    </div>
  );
}

function renderLandingTrendingShowcaseSkeleton() {
  return (
    <div
      className="relative overflow-hidden rounded-lg border border-white/10 bg-slate-950 shadow-xl shadow-black/30"
      data-testid="landing-trending-showcase-skeleton"
    >
      <div className="relative min-h-[23rem] md:min-h-[30rem]">
        <Skeleton className="absolute inset-0 h-full w-full rounded-none bg-white/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/72 to-slate-950/12" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/10 to-transparent" />
        <div className="relative z-10 flex min-h-[23rem] flex-col justify-between gap-6 p-4 md:min-h-[30rem] md:p-7 lg:p-8">
          <div className="flex w-full items-start gap-2.5 sm:gap-3 md:gap-4">
            <div className="flex w-[3.15rem] flex-col gap-1 rounded-[1rem] border border-white/15 bg-slate-950/58 p-1 sm:w-[3.65rem] md:w-[5rem] md:gap-2 md:p-2">
              <Skeleton className="h-7 rounded-lg bg-white/10 md:h-11" />
              <Skeleton className="h-7 rounded-lg bg-white/10 md:h-11" />
              <Skeleton className="h-7 rounded-lg bg-white/10 md:h-11" />
            </div>
            <div className="w-[clamp(7rem,29vw,13rem)] shrink-0 overflow-hidden rounded-lg border border-white/15 bg-slate-950/75 p-1 shadow-2xl shadow-black/35 md:w-[clamp(11rem,26vw,14rem)] lg:w-[clamp(14rem,21vw,17rem)]">
              <Skeleton className="aspect-[3/4] w-full rounded-md bg-white/10" />
            </div>
            <div className="min-w-0 flex-1 max-w-[32rem] lg:w-[min(16.5rem,24vw)] lg:max-w-none lg:flex-none">
              <div className="block rounded-xl border border-white/12 bg-slate-950/58 px-2.5 py-2 md:px-4 md:py-3">
                <div>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={`landing-trending-info-skeleton-${index}`}
                      className="grid grid-cols-1 gap-1 border-b border-white/10 py-1.5 lg:grid-cols-[6.1rem_minmax(0,1fr)] lg:gap-2 lg:py-2"
                    >
                      <Skeleton className="h-3 w-20 bg-white/10" />
                      <Skeleton className="h-3 w-full bg-white/10" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3 hidden w-fit max-w-full rounded-xl border border-white/12 bg-slate-950/54 px-3 py-2 shadow-xl shadow-black/35 lg:block">
                <Skeleton className="h-10 w-28 rounded-md bg-white/10" />
              </div>
            </div>
            <div className="ml-auto hidden min-w-[16rem] flex-1 max-w-[min(30rem,40vw)] rounded-xl border border-white/12 bg-slate-950/54 p-3 shadow-xl shadow-black/35 lg:block">
              <Skeleton className="aspect-video w-full rounded-lg bg-white/10" />
              <div className="mt-2.5 flex gap-2 overflow-hidden">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton
                    key={`landing-trending-screenshot-skeleton-${index}`}
                    className="h-12 w-20 shrink-0 rounded-lg bg-white/10"
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="-mt-4 hidden self-start items-center gap-2 md:flex">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={`landing-trending-official-link-skeleton-${index}`}
                className="h-10 w-10 rounded-lg bg-white/10"
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Skeleton className="h-1.5 w-8 rounded-full bg-cyan-200/60" />
            <Skeleton className="h-1.5 w-3 rounded-full bg-white/30" />
            <Skeleton className="h-1.5 w-3 rounded-full bg-white/30" />
          </div>
        </div>
      </div>
    </div>
  );
}

function renderLandingHighHypeCountdownSkeleton() {
  return (
    <div
      className="relative overflow-hidden rounded-lg border border-cyan-300/20 bg-slate-950 shadow-2xl shadow-black/35"
      data-testid="landing-high-hype-countdown-skeleton"
    >
      <div className="grid min-h-[34rem] gap-5 p-4 sm:p-5 md:min-h-[35rem] md:p-7 lg:grid-cols-[minmax(0,0.92fr)_minmax(28rem,1.25fr)] lg:gap-7 lg:p-8">
        <div className="flex flex-col justify-between gap-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Skeleton className="h-7 w-32 rounded-full bg-white/10" />
              <Skeleton className="h-7 w-24 rounded-full bg-white/10" />
            </div>
            <div className="flex items-start gap-4">
              <Skeleton className="hidden h-28 w-20 rounded-lg bg-white/10 sm:block" />
              <div className="w-full space-y-3">
                <Skeleton className="h-9 w-10/12 bg-white/10" />
                <Skeleton className="h-5 w-7/12 bg-white/10" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-2 sm:grid-cols-4 sm:gap-3 sm:p-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={`landing-high-hype-countdown-tile-${index}`} className="h-28 rounded-xl bg-white/10" />
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={`landing-high-hype-countdown-thumb-${index}`} className="h-12 rounded-lg bg-white/10" />
            ))}
          </div>
        </div>
        <Skeleton className="min-h-[18rem] rounded-2xl bg-white/10 lg:min-h-0" />
      </div>
    </div>
  );
}

function renderLandingLatestNewsCompactSkeleton(count = 2) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <div key={`landing-news-compact-skeleton-${index}`} className="glass-effect overflow-hidden rounded-lg">
          <Skeleton className="h-[23rem] w-full rounded-none bg-white/10 sm:h-80" />
          <div className="min-h-[11.5rem] space-y-3 p-5">
            <Skeleton className="h-5 w-11/12 bg-white/10" />
            <Skeleton className="h-5 w-8/12 bg-white/10" />
            <Skeleton className="h-4 w-full bg-white/10" />
            <Skeleton className="h-4 w-9/12 bg-white/10" />
            <div className="flex items-center justify-between pt-2">
              <Skeleton className="h-3 w-12 bg-white/10" />
              <Skeleton className="h-6 w-24 rounded-full bg-white/10" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function renderLandingLatestNewsDesktopSkeleton(count = 3) {
  return (
    <div>
      <div className="flex gap-6 overflow-hidden px-4 py-4">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={`landing-news-desktop-skeleton-${index}`}
            className="w-[calc((100%_-_96px)/5)] min-w-[calc((100%_-_96px)/5)] overflow-hidden rounded-lg"
          >
            <div className="glass-effect overflow-hidden rounded-lg">
              <Skeleton className="h-80 w-full rounded-none bg-white/10 lg:h-[21rem]" />
              <div className="min-h-[11.5rem] space-y-3 p-5">
                <Skeleton className="h-5 w-10/12 bg-white/10" />
                <Skeleton className="h-5 w-7/12 bg-white/10" />
                <Skeleton className="h-4 w-full bg-white/10" />
                <Skeleton className="h-4 w-9/12 bg-white/10" />
                <div className="flex items-center justify-between pt-2">
                  <Skeleton className="h-3 w-12 bg-white/10" />
                  <Skeleton className="h-6 w-24 rounded-full bg-white/10" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderLandingLatestNewsFallback(isDesktop) {
  return isDesktop
    ? renderLandingLatestNewsDesktopSkeleton(3)
    : renderLandingLatestNewsCompactSkeleton(2);
}

export default function Landing() {
  const { t } = useTranslation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { isPrivilegedUser, isSelective } = usePublication();
  const { getFlag, isFlagActive, isLoading: areFeatureFlagsLoading } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMdUp = useMediaQuery('(min-width: 768px)');
  const isLgUp = useMediaQuery('(min-width: 1024px)');
  const isSelectivePublic = shouldUseSelectivePublicAccountMode({
    isSelective,
    isPrivilegedUser,
  });
  const shouldDeferBelowFoldSectionsOnMobile = !isMdUp;
  const deferredSectionStyle = shouldDeferBelowFoldSectionsOnMobile
    ? { contentVisibility: 'auto', containIntrinsicSize: '920px' }
    : undefined;
  const deferredNewsSectionStyle = shouldDeferBelowFoldSectionsOnMobile
    ? { contentVisibility: 'auto', containIntrinsicSize: '1040px' }
    : undefined;
  const deferredCompactSectionStyle = shouldDeferBelowFoldSectionsOnMobile
    ? { contentVisibility: 'auto', containIntrinsicSize: '720px' }
    : undefined;
  const showTrendingAgeRatingPreview = import.meta.env.DEV
    && (
      new URLSearchParams(location.search).has('codex_age_rating_preview')
      || new URLSearchParams(location.search).has('codex_trending_visual_preview')
    );
  const showLatestNewsPreview = import.meta.env.DEV
    && new URLSearchParams(location.search).has('codex_latest_news_preview');
  const showHighHypeCountdownPreview = import.meta.env.DEV
    && new URLSearchParams(location.search).has('codex_high_hype_preview');
  const showReleaseDeckPreview = import.meta.env.DEV
    && new URLSearchParams(location.search).has('codex_release_deck_preview');
  const showComingSoonRunwayPreview = import.meta.env.DEV
    && new URLSearchParams(location.search).has('codex_coming_runway_preview');

  const [searchTerm, setSearchTerm] = useState('');
  const [featuredGames, setFeaturedGames] = useState([]);
  const [comingSoonGames, setComingSoonGames] = useState([]);
  const [highHypeUpcomingGames, setHighHypeUpcomingGames] = useState([]);
  const [recentlyReleasedGames, setRecentlyReleasedGames] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(LANDING_SECTION_IDS.hero);
  const [isMobileSectionNavOpen, setIsMobileSectionNavOpen] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const mobileSectionNavTriggerRef = useRef(null);

  const handleMobileSectionNavToggle = useCallback((nextOpen) => {
    setIsMobileSectionNavOpen(nextOpen);

    if (nextOpen || typeof window === 'undefined') {
      return;
    }

    const restoreFocus = () => mobileSectionNavTriggerRef.current?.focus();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(restoreFocus);
      return;
    }

    window.setTimeout(restoreFocus, 0);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAllData = async () => {
      try {
        const response = await fetch('/api/landing-content', {
          method: 'GET',
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || 'landing_content_fetch_failed');
        }

        setFeaturedGames(Array.isArray(payload.featuredGames) ? payload.featuredGames : []);
        setComingSoonGames(Array.isArray(payload.comingSoonGames) ? payload.comingSoonGames : []);
        setHighHypeUpcomingGames(Array.isArray(payload.highHypeUpcomingGames) ? payload.highHypeUpcomingGames : []);
        setRecentlyReleasedGames(Array.isArray(payload.recentlyReleasedGames) ? payload.recentlyReleasedGames : []);
        setLatestPosts(Array.isArray(payload.latestPosts) ? payload.latestPosts : []);
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
        console.error('Error fetching landing page data:', error);
        setFeaturedGames([]);
        setComingSoonGames([]);
        setHighHypeUpcomingGames([]);
        setRecentlyReleasedGames([]);
        setLatestPosts([]);
      }
    };

    fetchAllData();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('newsletter') !== 'confirmed') {
      return;
    }

    toast({
      title: t('landing.newsletter_confirmed_title'),
      description: t('landing.newsletter_confirmed_body'),
    });

    params.delete('newsletter');
    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate, toast, t]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!isMobileSectionNavOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleMobileSectionNavToggle(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleMobileSectionNavToggle, isMobileSectionNavOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const syncFooterVisibility = () => {
      const footer = document.querySelector('footer');
      if (!footer) {
        return;
      }
      const rect = footer.getBoundingClientRect();
      const nextVisible = rect.top < window.innerHeight && rect.bottom > 0;
      setIsFooterVisible((current) => (current === nextVisible ? current : nextVisible));
      if (nextVisible) {
        setIsMobileSectionNavOpen(false);
      }
    };

    let observer;
    const observeFooter = () => {
      const footer = document.querySelector('footer');
      if (!footer || typeof window.IntersectionObserver !== 'function') {
        syncFooterVisibility();
        return;
      }

      observer = new window.IntersectionObserver((entries) => {
        const nextVisible = entries.some((entry) => entry.isIntersecting);
        setIsFooterVisible((current) => (current === nextVisible ? current : nextVisible));
        if (nextVisible) {
          setIsMobileSectionNavOpen(false);
        }
      }, { threshold: 0.01 });
      observer.observe(footer);
      syncFooterVisibility();
    };

    const observationFrame = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(observeFooter)
      : null;

    syncFooterVisibility();
    window.addEventListener('scroll', syncFooterVisibility, { passive: true });
    window.addEventListener('resize', syncFooterVisibility);
    return () => {
      observer?.disconnect();
      if (observationFrame !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(observationFrame);
      }
      window.removeEventListener('scroll', syncFooterVisibility);
      window.removeEventListener('resize', syncFooterVisibility);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      trackPublicTelemetryAction(PUBLIC_TELEMETRY_ACTIONS.LANDING_GAMES_SEARCH);
      navigate(createPageUrl(`games${createGamesSearchString({ searchTerm: searchTerm.trim() })}`));
    }
  };

  const canonicalUrl = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/`;
    }
    return `${DEFAULT_ORIGIN}/`;
  }, []);

  const pageUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;

  const seoTitle = t('landing.seo.title') || `${t('landing.title')} - GameplaySync`;
  const seoDescription = t('landing.seo.description') || t('landing.teaser');
  const seoKeywords = useMemo(() => {
    const rawValue = t('landing.seo.keywords');
    if (!rawValue || typeof rawValue !== 'string') {
      return [];
    }
    return rawValue
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  }, [t]);
  const ogImageUrl = useMemo(() => {
    const normalizedOrigin = canonicalUrl.replace(/\/$/, '');
    return `${normalizedOrigin}/assets/og/landing.png`;
  }, [canonicalUrl]);

  const structuredData = useMemo(() => {
    const normalizedOrigin = canonicalUrl.replace(/\/$/, '');
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'GameplaySync',
        url: `${normalizedOrigin}/`,
        logo: `${normalizedOrigin}/assets/logo.png`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'GameplaySync',
        url: `${normalizedOrigin}/`,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${normalizedOrigin}/games?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${normalizedOrigin}/`,
          },
        ],
      },
    ];
  }, [canonicalUrl]);

  const heroTitleClassName = 'mb-4';
  const heroSubtitleClassName = 'gps-brand-teaser mb-8 text-xl sm:text-2xl lg:text-3xl';

  const heroSection = (
    <div className="relative overflow-hidden" id={LANDING_SECTION_IDS.hero}>
      <div className="absolute inset-0 bg-gradient-to-r from-purple-800/20 to-blue-800/20" />
      <div className="relative mx-auto flex min-h-[calc(100svh-4rem)] max-w-7xl flex-col justify-center px-4 py-16 text-center md:min-h-0 md:py-20 lg:py-32">
        <h1 className={heroTitleClassName}>
          <span className="gps-brand-wordmark gps-brand-wordmark--hero">{t('landing.title')}</span>
        </h1>
        <p className={heroSubtitleClassName}>
          {ENGLISH_HERO_SUBTITLE}
        </p>
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-6">
          <div className="relative flex items-center">
            <Search className="absolute left-4 w-5 h-5 text-[var(--primary)]" />
            <Input
              type="search"
              placeholder={t('landing.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-12 pl-12 pr-24 rounded-full bg-[var(--glass-bg)] border-[var(--glass-border)]"
            />
            <Button type="submit" className="absolute right-0 h-12 rounded-r-full bg-[var(--primary)] text-white px-6">
              {t('landing.search_button')}
            </Button>
          </div>
        </form>
        <Link
          to={createPageUrl('games')}
          onClick={() => trackPublicTelemetryAction(PUBLIC_TELEMETRY_ACTIONS.OPEN_GAMES)}
        >
          <Button variant="outline" className="min-h-11 border-[var(--accent)] text-[var(--accent)]">
            <Library className="mr-2 h-4 w-4" /> {t('landing.explore_games')}
          </Button>
        </Link>
      </div>
    </div>
  );

  const recentReleaseDeckGames = showReleaseDeckPreview
    ? landingRecentReleaseDeckPreviewGames
    : recentlyReleasedGames;

  const recentReleasesContent = recentReleaseDeckGames.length > 0
    ? (
      <Suspense fallback={isMdUp ? renderLandingGameGridSkeleton(6) : renderLandingGameCarouselSkeleton(2)}>
        <LandingRecentReleaseDeck
          games={recentReleaseDeckGames}
          imageSizes={landingGameImageSizes}
          imageWidth={isLgUp ? 360 : isMdUp ? 320 : 260}
        />
      </Suspense>
    )
    : (
      isMdUp ? renderLandingGameGridSkeleton(10) : renderLandingGameCarouselSkeleton(2)
    );

  const trendingShowcaseGames = featuredGames.length > 0
    ? featuredGames
    : showTrendingAgeRatingPreview
      ? landingTrendingAgeRatingPreviewGames
      : [];

  const trendingContent = trendingShowcaseGames.length > 0
    ? (
      <Suspense fallback={renderLandingTrendingShowcaseSkeleton()}>
        <LandingTrendingShowcase
          ariaLabel={t('landing.trending_title')}
          games={trendingShowcaseGames}
          showAgeRatingPreview={showTrendingAgeRatingPreview}
        />
      </Suspense>
    )
    : (
      renderLandingTrendingShowcaseSkeleton()
    );

  const latestNewsItems = showLatestNewsPreview
    ? landingLatestNewsPreviewPosts
    : latestPosts;

  const latestNewsContent = latestNewsItems.length > 0
    ? (
      isLgUp ? (
        <Suspense fallback={renderLandingLatestNewsFallback(true)}>
          <ArticleCarousel
            articles={latestNewsItems}
            autoScrollInterval={4000}
            cardVariant="landingLatest"
            progressLabel={t('landing.latest_news_title')}
            resetLabel={t('landing.latest_news_restart')}
            showProgressBar
          />
        </Suspense>
      ) : (
        <Suspense fallback={renderLandingLatestNewsFallback(false)}>
          <ArticleCarousel
            articles={latestNewsItems}
            autoScrollInterval={4800}
            cardVariant="landingLatest"
            mobileCardWidthClass="w-[84vw] max-w-[22.5rem]"
            mobileSnapClass="snap-center"
            mobileTrackPaddingClass="px-[8vw] sm:px-0"
            progressLabel={t('landing.latest_news_title')}
            resetLabel={t('landing.latest_news_restart')}
            showProgressBar
          />
        </Suspense>
      )
    )
    : (
      isLgUp
        ? renderLandingLatestNewsDesktopSkeleton(3)
        : renderLandingLatestNewsCompactSkeleton(2)
    );
  const latestNewsSectionContent = (
    <>
      {latestNewsContent}
      {isSelectivePublic ? (
        <div className="mt-8 flex justify-center">
          <Link
            to={createPageUrl('news/archive')}
            onClick={() => trackPublicTelemetryAction(PUBLIC_TELEMETRY_ACTIONS.OPEN_NEWS_ARCHIVE)}
          >
            <Button
              className="min-h-11 rounded-full border-cyan-200/35 bg-cyan-300/10 px-6 text-cyan-50 hover:bg-cyan-300/20"
              type="button"
              variant="outline"
            >
              {t('landing.latest_news_archive_cta')}
            </Button>
          </Link>
        </div>
      ) : null}
    </>
  );

  const comingSoonRunwayGames = showComingSoonRunwayPreview
    ? landingComingSoonRunwayPreviewGames
    : comingSoonGames;

  const comingSoonContent = comingSoonRunwayGames.length > 0
    ? (
      <Suspense fallback={renderLandingComingSoonRunwaySkeleton()}>
        <LandingComingSoonRunway
          games={comingSoonRunwayGames}
          imageSizes={landingGameImageSizes}
          imageWidth={isLgUp ? 320 : 280}
        />
      </Suspense>
    )
    : renderLandingComingSoonRunwaySkeleton();
  const highHypeCountdownGames = highHypeUpcomingGames.length > 0
    ? highHypeUpcomingGames
    : showHighHypeCountdownPreview
      ? landingHighHypeCountdownPreviewGames
      : [];
  const highHypeCountdownContent = highHypeCountdownGames.length > 0
    ? (
      <Suspense fallback={renderLandingHighHypeCountdownSkeleton()}>
        <LandingHighHypeCountdownShowcase games={highHypeCountdownGames} />
      </Suspense>
    )
    : renderLandingHighHypeCountdownSkeleton();
  const socialWallPreviewFlag = getFlag(LANDING_SOCIAL_WALL_PREVIEW_FLAG);
  const showSocialWallPreview =
    isPrivilegedUser &&
    !areFeatureFlagsLoading &&
    (isFlagActive(LANDING_SOCIAL_WALL_PREVIEW_FLAG) || !socialWallPreviewFlag);
  const isNewsletterPopupEnabled =
    !isAuthLoading &&
    !areFeatureFlagsLoading &&
    isFlagActive('newsletter_footer_enabled');
  const { isNewsletterPopupOpen, closeNewsletterPopup } = useNewsletterPopupTrigger({
    enabled: isNewsletterPopupEnabled,
    isRegisteredUser: Boolean(user),
  });

  const pageNavSections = useMemo(() => [
    {
      id: LANDING_SECTION_IDS.hero,
      label: t('landing.section_nav.hero'),
    },
    {
      id: LANDING_SECTION_IDS.trending,
      label: t('landing.trending_title'),
    },
    {
      id: LANDING_SECTION_IDS.recentlyReleased,
      label: t('landing.recently_released_title'),
    },
    {
      id: LANDING_SECTION_IDS.latestNews,
      label: t('landing.latest_news_title'),
    },
    {
      id: LANDING_SECTION_IDS.mostWanted,
      label: t('landing.hype_countdown.title'),
    },
    {
      id: LANDING_SECTION_IDS.comingSoon,
      label: t('landing.coming_soon_title'),
    },
    ...(showSocialWallPreview
      ? [
          {
            id: LANDING_SECTION_IDS.socialWall,
            label: t('landing.social_wall.title'),
          },
        ]
      : []),
    ...(!isSelectivePublic
      ? [
          {
            id: LANDING_SECTION_IDS.live,
            label: t('landing.section_nav.live'),
          },
        ]
      : []),
    {
      id: LANDING_SECTION_IDS.cta,
      label: isSelectivePublic ? t('nav.deal_finder') : t('landing.section_nav.cta'),
    },
  ], [isSelectivePublic, showSocialWallPreview, t]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || pageNavSections.length === 0) {
      return undefined;
    }

    const sectionElements = pageNavSections
      .map((section) => document.getElementById(section.id))
      .filter(Boolean);

    if (sectionElements.length === 0) {
      return undefined;
    }

    const syncActiveSection = () => {
      const marker = window.innerHeight < 720 ? 112 : 150;
      const activeElement = sectionElements.reduce((current, element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top <= marker) {
          return element;
        }
        return current;
      }, sectionElements[0]);

      setActiveSectionId((current) => (current === activeElement.id ? current : activeElement.id));
    };

    if (typeof window.IntersectionObserver !== 'function') {
      syncActiveSection();
      window.addEventListener('scroll', syncActiveSection, { passive: true });
      return () => window.removeEventListener('scroll', syncActiveSection);
    }

    const observer = new window.IntersectionObserver((entries) => {
      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort((left, right) => Math.abs(left.boundingClientRect.top) - Math.abs(right.boundingClientRect.top));

      if (visibleEntries[0]?.target?.id) {
        setActiveSectionId((current) => (
          current === visibleEntries[0].target.id ? current : visibleEntries[0].target.id
        ));
      }
    }, {
      rootMargin: '-34% 0px -56% 0px',
      threshold: [0, 0.12, 0.4],
    });

    sectionElements.forEach((element) => observer.observe(element));
    syncActiveSection();

    return () => observer.disconnect();
  }, [pageNavSections]);

  const handleSectionNavigate = useCallback((sectionId) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const target = document.getElementById(sectionId);
    if (!target) {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    const shouldUseInstantScroll = prefersReducedMotion || window.innerWidth < 1024;
    const offset = window.innerWidth < 1024 ? 88 : 104;
    const top = Math.max(0, target.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - offset);

    setActiveSectionId(sectionId);
    handleMobileSectionNavToggle(false);
    window.scrollTo({
      behavior: shouldUseInstantScroll ? 'auto' : 'smooth',
      left: 0,
      top,
    });
  }, [handleMobileSectionNavToggle]);

  const sectionNavigatorControl = (
    <LandingSectionNavigator
      activeSectionId={activeSectionId}
      hideMobile={isFooterVisible}
      isMobileOpen={isMobileSectionNavOpen}
      onNavigate={handleSectionNavigate}
      onToggleMobile={handleMobileSectionNavToggle}
      sections={pageNavSections}
      t={t}
      triggerRef={mobileSectionNavTriggerRef}
    />
  );

  return (
    <div
      className="min-h-screen bg-[var(--background)]"
      data-selective-surface="landing"
    >
      <SeoManager
        title={seoTitle}
        description={seoDescription}
        imageUrl={ogImageUrl}
        pageUrl={pageUrl}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
        keywords={seoKeywords}
      />
      {sectionNavigatorControl}
      {heroSection}

      {renderLandingSection(
        t('landing.trending_title'),
        trendingContent,
        landingTrendingShowcaseMinHeightClass,
        { id: LANDING_SECTION_IDS.trending },
      )}

      {renderLandingSection(
        t('landing.recently_released_title'),
        recentReleasesContent,
        landingGamesSectionMinHeightClass,
        { id: LANDING_SECTION_IDS.recentlyReleased, sectionStyle: deferredSectionStyle },
      )}

      {renderLandingSection(
        t('landing.latest_news_title'),
        latestNewsSectionContent,
        landingNewsSectionMinHeightClass,
        { id: LANDING_SECTION_IDS.latestNews, sectionStyle: deferredNewsSectionStyle },
      )}

      {renderLandingSection(
        t('landing.hype_countdown.title'),
        highHypeCountdownContent,
        landingHighHypeCountdownMinHeightClass,
        { id: LANDING_SECTION_IDS.mostWanted, sectionStyle: deferredCompactSectionStyle },
      )}

      {renderLandingSection(
        t('landing.coming_soon_title'),
        comingSoonContent,
        landingComingSoonSectionMinHeightClass,
        { id: LANDING_SECTION_IDS.comingSoon, sectionStyle: deferredSectionStyle },
      )}

      {showSocialWallPreview && renderLandingSection(
        t('landing.social_wall.title'),
        <Suspense fallback={null}>
          <SocialWallLandingPreview />
        </Suspense>,
        "min-h-[24rem] sm:min-h-[26rem] lg:min-h-[28rem]",
        { id: LANDING_SECTION_IDS.socialWall, sectionStyle: deferredCompactSectionStyle },
      )}

      {!isSelectivePublic ? (
        <div id={LANDING_SECTION_IDS.live} style={deferredCompactSectionStyle}>
          <Suspense fallback={null}>
            <OfficialLiveTeaser />
          </Suspense>
        </div>
      ) : null}

      {/* Primary product CTA */}
      <section id={LANDING_SECTION_IDS.cta} className="relative px-4 py-20" style={deferredCompactSectionStyle}>
        <div className="max-w-5xl mx-auto">
          <div className="glass-effect relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 sm:p-12 text-center">
            <div className="absolute -top-24 right-12 h-48 w-48 rounded-full bg-[var(--microaccent)]/20 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-20 left-8 h-40 w-40 rounded-full bg-[var(--accent)]/20 blur-3xl" aria-hidden="true" />
            <div className="relative space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                <Sparkles className="h-4 w-4 text-[var(--accent)]" />
                {isSelectivePublic ? t('landing.selective_deal_finder.badge') : t('footer.cta.badge')}
              </div>
              <h2 className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                {isSelectivePublic ? t('landing.selective_deal_finder.title') : t('footer.cta.title')}
              </h2>
              <p className="text-base text-white/70 sm:text-lg max-w-2xl mx-auto">
                {isSelectivePublic ? t('landing.selective_deal_finder.description') : t('footer.cta.subtitle')}
              </p>
              <div className="flex justify-center">
                <Link to={createPageUrl(isSelectivePublic ? 'deal-finder' : 'pricing')}>
                  <Button
                    type="button"
                    className="landing-early-access-button relative inline-flex min-h-11 items-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-[var(--primary)] via-[var(--microaccent)] to-[var(--secondary)] px-8 py-4 text-base font-semibold text-white transition hover:brightness-110"
                  >
                    {isSelectivePublic ? <Search className="h-5 w-5" /> : <Joystick className="h-5 w-5" />}
                    {isSelectivePublic ? t('landing.selective_deal_finder.button') : t('footer.cta.button')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Participation CTA */}
      {isSelectivePublic ? (
        <section className="px-4 py-20 text-center" style={deferredCompactSectionStyle}>
          <h2 className="mb-4 text-3xl font-bold font-orbitron">
            {user
              ? t('landing.participation.authenticated_title')
              : t('landing.participation.guest_title')}
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-[var(--text)]/80">
            {user
              ? t('landing.participation.authenticated_description')
              : t('landing.participation.guest_description')}
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            {user ? (
              <>
                <Link to={createPageUrl('games')}>
                  <Button size="lg" className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white font-bold">
                    <Play className="mr-2 h-5 w-5" /> {t('landing.participation.games_button')}
                  </Button>
                </Link>
                <Link to={createPageUrl('news')}>
                  <Button size="lg" variant="outline">
                    {t('landing.participation.news_button')}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to={createPageUrl('signup')}>
                  <Button size="lg" className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white font-bold">
                    {t('landing.participation.signup_button')}
                  </Button>
                </Link>
                <Link to={createPageUrl('login')}>
                  <Button size="lg" variant="outline">
                    {t('landing.participation.login_button')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </section>
      ) : (
        <div className="px-4 py-20 text-center" style={deferredCompactSectionStyle}>
          <h2 className="text-3xl font-bold font-orbitron mb-4">{t('landing.cta_title')}</h2>
          <p className="text-[var(--text)]/80 mb-8 max-w-xl mx-auto">{t('landing.cta_desc')}</p>
          <Link to={createPageUrl('games')}>
            <Button size="lg" className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white font-bold">
              <Play className="mr-2 h-5 w-5" /> {t('landing.cta_button')}
            </Button>
          </Link>
        </div>
      )}

      {isNewsletterPopupOpen ? (
        <Suspense fallback={null}>
          <NewsletterSignupPopup
            open
            onClose={closeNewsletterPopup}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
